import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Modal } from 'react-bootstrap';
import { isEmpty } from 'lodash';

import { usePagination } from '../../../hooks';
import {
    deleteMyDocumentsAPI,
    getAllMyDocumentsAPI,
} from '../../../utils/apiServices/myDocumentsAPI';
import { setSelectedSafetyMeetings } from '../../../redux';
import { getLocalStorageItem, popup } from '../../../utils';
import InputWithSearch from '../../shared/common/InputWithSearch';
import { ConfirmationBox, Table } from '../../shared';
import { DELETED_MY_DOCUMENTS_COLUMNS, LOCAL_STORAGE, QUERY_KEYS } from '../../../constants';
import { RestoreDocumentModal } from './RestoreDocumentModal';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';

const DeletedDocumentsModal = ({
    showModal,
    setShowModal,
    searchBarPlaceholder = 'Search...',
    pageName = '',
}) => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const accountId = getLocalStorageItem(LOCAL_STORAGE.CUSTOMER_ID);
    const [deletedDocuments, setDeletedDocuments] = useState({ data: [], count: 0 });
    const [search, setSearch] = useState('');
    const pagination = usePagination(deletedDocuments?.data.length, deletedDocuments?.count);
    const selectedSafetyMeetings = useSelector((s) => s.safetyMeetings.selectedSafetyMeetings);
    const [isRestoreOpen, setIsRestoreOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    const [debounceSearch] = useDebouncedValue(search, 500);
    const { data, isFetching, isFetched, isError, error } = useQuery({
        queryKey: [
            QUERY_KEYS.MY_DELETED_DOCUMENTS,
            accountId,
            debounceSearch,
            pagination.limit,
            pagination.offset,
        ],
        queryFn: async () =>
            await await getAllMyDocumentsAPI({
                limit: pagination.limit,
                offset: pagination.offset,
                search: debounceSearch,
                only_deleted: true,
                account_id: accountId,
            }),
        select: (data) => ({ data: data.data, count: data.count }),
        placeholderData: { data: [], count: 0 },
        enabled: !!accountId && showModal,
    });

    useEffect(() => {
        if (data.data && isFetched) {
            setDeletedDocuments({ data: data.data, count: data.count });
        }
    }, [data, isFetched]);

    if (isError) popup('error', error.message);

    const permanentDeleteMutation = useMutation({
        mutationFn: async () =>
            await deleteMyDocumentsAPI({
                data: { ids: selectedSafetyMeetings?.map((doc) => doc?.id), permanently: true },
            }),
        onSuccess: ({ message }) => {
            dispatch(setSelectedSafetyMeetings([]));
            popup('success', message);
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.MY_DELETED_DOCUMENTS],
                exact: false,
            });
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });

    const permanentDeleteDocument = (v) => {
        if (v) {
            permanentDeleteMutation.mutate();
        }
    };

    return (
        <Modal
            show={showModal}
            onHide={() => {
                setShowModal(false);
                dispatch(setSelectedSafetyMeetings([]));
            }}
            size='lg'
            dialogClassName='overflow-hidden'
            fullscreen='md-down'
        >
            <Modal.Header closeButton>
                <Modal.Title className='me-3' style={{ fontSize: '18px' }}>
                    {pageName}
                </Modal.Title>
                <InputWithSearch
                    search={search}
                    setSearch={setSearch}
                    placeholder={searchBarPlaceholder}
                    className={'w-50'}
                    isLoading={isFetching}
                />
            </Modal.Header>
            <Modal.Body className='position-relative' style={{ minHeight: '42.75rem' }}>
                {!isEmpty(deletedDocuments) && (
                    <Table
                        columns={DELETED_MY_DOCUMENTS_COLUMNS}
                        data={deletedDocuments?.data}
                        pagination={pagination}
                        tableStyle={{
                            height: '36.25rem',
                            overflow: 'auto',
                        }}
                        stickyColumns={[DELETED_MY_DOCUMENTS_COLUMNS?.[1].name]}
                        pageName={pageName}
                        isLoading={isFetching}
                    />
                )}
                {isRestoreOpen && (
                    <RestoreDocumentModal
                        showModal={isRestoreOpen}
                        setShowModal={setIsRestoreOpen}
                    />
                )}
                {setDeleteModalOpen && (
                    <ConfirmationBox
                        setConfirm={(v) => permanentDeleteDocument(v)}
                        openConfirmationBox={deleteModalOpen}
                        setOpenConfirmationBox={setDeleteModalOpen}
                        customMessage={`Are you sure you want to PERMANENTLY DELETE ${selectedSafetyMeetings.length} file(s)?`}
                    />
                )}
            </Modal.Body>
            <Modal.Footer>
                <div className='d-flex w-100 align-items-center justify-content-between modal-btn'>
                    <span
                        style={{ color: '#738da6', fontSize: '14px' }}
                    >{`${selectedSafetyMeetings.length} selected`}</span>
                    <span className='d-flex gap-3'>
                        <Button
                            type='button'
                            onClick={() => setDeleteModalOpen(true)}
                            className={`fw-medium border-danger w-auto bg-danger ${selectedSafetyMeetings.length === 0 ? 'btn-disabled' : ''}`}
                            disabled={selectedSafetyMeetings.length === 0}
                        >
                            Permanent Delete
                        </Button>
                        <Button
                            type='button'
                            onClick={() => setIsRestoreOpen(true)}
                            className='table-footer-btn'
                            disabled={selectedSafetyMeetings.length === 0}
                        >
                            Restore
                        </Button>
                    </span>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default DeletedDocumentsModal;
